import React from "react";
import styled from "styled-components";
import { Blade } from "../../../components/Blade";
import { mixinSkins } from "../../../skins/shared/mixins/themeStyles";
import { SkinId } from "../../../skins/types";
import { riotProductSkin } from "../skins/riot-product";
import { BladeContent } from "../../../components/BladeContent";
import { z } from "zod";
import { AudioItemBladeSchema } from "../../../schemas/blades/AudioItem";
import { Media } from "../../../components/Media";
import { formatDuration, safeMediaPlay } from "./utils";
export const AudioPlayerContractSchema = AudioItemBladeSchema.extend({
    type: z.literal("audioPlayer"),
});
const _AudioPlayer = ({ className, audio, caption, ...bladeProps }) => {
    const [playing, setPlaying] = React.useState(false);
    const [muted, setMuted] = React.useState(false);
    const [time, setTime] = React.useState(0);
    const [remainingTime, setRemainingTime] = React.useState("00:00");
    const audioRef = React.useRef(null);
    const SLIDER_RANGE_MAX = 10000;
    React.useEffect(() => {
        const timer = setInterval(() => {
            if (audioRef.current) {
                const secondsRemaining = audioRef.current.duration - audioRef.current.currentTime;
                if (secondsRemaining === 0) {
                    setRemainingTime(formatDuration(audioRef.current.duration));
                    setTime(0);
                }
                else {
                    setRemainingTime(formatDuration(secondsRemaining));
                    setTime(Math.floor((audioRef.current.currentTime / audioRef.current.duration) * SLIDER_RANGE_MAX));
                }
            }
        }, 100);
        return () => clearTimeout(timer);
    }, []);
    const playOrPause = React.useCallback(() => {
        setPlaying(!playing);
        if (audioRef.current) {
            if (!playing)
                safeMediaPlay(audioRef.current).play();
            else
                audioRef.current.pause();
        }
    }, [playing]);
    const muteOrUnmute = React.useCallback(() => {
        setMuted(!muted);
        if (audioRef.current) {
            audioRef.current.muted = !muted;
        }
    }, [muted]);
    const onEnded = React.useCallback(() => {
        setPlaying(false);
    }, []);
    return (React.createElement(Blade, { className: className, ...bladeProps },
        React.createElement(BladeContent, { width: "medium" },
            React.createElement("div", { className: "audio-player-container" },
                React.createElement("button", { type: "button", "data-testid": "audio-player-play-pause-btn", className: "play-pause-bg", onClick: playOrPause, "aria-label": playing ? "Pause" : "Play" }, playing ? (React.createElement("span", { "data-testid": "audio-player-pause", className: "pause" })) : (React.createElement("span", { "data-testid": "audio-player-play", className: "play" }))),
                React.createElement("div", { className: "progress-container" },
                    React.createElement("div", { "data-testid": "audio-player-progress-bar", className: "progress-bar", style: { width: `${(time / SLIDER_RANGE_MAX) * 100}%` } })),
                React.createElement("div", { className: "audio-player-right-addons" },
                    React.createElement("div", { "data-testid": "remaining-time", className: "remaining-time" }, remainingTime || "00:00"),
                    React.createElement("button", { type: "button", "data-testid": "audio-player-mute-unmute-btn", className: "mute-unmute", onClick: muteOrUnmute, "aria-label": muted ? "Unmute" : "Mute" }, muted ? (React.createElement("span", { "data-testid": "audio-player-unmute", className: "unmute" })) : (React.createElement("span", { "data-testid": "audio-player-mute", className: "mute" })))),
                caption ? (React.createElement("div", { "data-testid": "audio-player-caption", className: "audio-player-caption" }, caption)) : null),
            React.createElement(Media, { media: audio, ref: audioRef, onEnded: onEnded }))));
};
export const AudioPlayer = styled(_AudioPlayer) `
  ${mixinSkins({
    [SkinId.RiotProduct]: riotProductSkin,
})}
`;
//# sourceMappingURL=AudioPlayer.js.map